import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, X, Loader2, Warehouse, Package, ArrowDownCircle, ArrowUpCircle,
  RefreshCw, AlertTriangle, CheckCircle, Boxes, ScanBarcode,
  ArrowDown, ArrowUp, Hash, ClipboardCheck, ChevronRight, XCircle,
  Clock, User, ClipboardList, Truck, Eye, EyeOff,
  MapPin, Flag, Check, TrendingDown, TrendingUp, Settings2, ShoppingCart, Search, Zap,
  ChevronDown, Activity, PackageCheck, PackageX, BarChart3,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { LocationGradePivot } from './LocationGradePivot';
import { StockFilters, EMPTY_STOCK_FILTER, stockFilterParams, type StockFilterValue } from './StockFilters';
import { RawMaterialsPanel } from './RawMaterialsPanel';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  name: string;
  sku: string;
  category?: string;
  productType?: string | null;
  stockQty?: number;
  reorderLevel?: number;
  unit?: string;
  imageUrl?: string;
  images?: string[];
  specifications?: Record<string, any>;
  inventory?: Array<{ quantity: number; warehouse: string }>;
}

interface StockMovement {
  id: string;
  date?: string;
  createdAt?: string;
  productId: string;
  productName?: string;
  product?: { name: string };
  type: 'in' | 'out' | 'adjustment';
  quantity: number;
  reference?: string;
  reason?: string;
  notes?: string;
}

interface Batch {
  id: string;
  batchNumber: string;
  productId: string;
  productName?: string;
  product?: { name: string };
  manufacturingDate?: string;
  mfgDate?: string;
  expiryDate?: string;
  totalQty?: number;
  remainingQty?: number;
  quantity?: number;
  costPrice?: number;
  status: string;
}

interface ScanHistoryEntry {
  time: string;
  barcode: string;
  productName: string;
  action: string;
  qty: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'stock', label: 'Stock Overview', icon: Package },
  { key: 'raw_material', label: 'Raw Material', icon: Boxes },
  { key: 'sfg', label: 'Semi-Finished', icon: Activity },
  { key: 'fg', label: 'Finished Goods', icon: PackageCheck },
  { key: 'replenishment', label: 'Replenishment', icon: TrendingDown },
  { key: 'movements', label: 'Stock Movements', icon: RefreshCw },
  { key: 'batches', label: 'Batch Tracking', icon: Boxes },
  { key: 'scanner', label: 'Barcode Scanner', icon: ScanBarcode },
  { key: 'qc', label: 'QC Checkpoints', icon: ClipboardCheck },
  { key: 'audit', label: 'Stock Audit', icon: ClipboardList },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const MOVEMENT_TYPE_COLORS: Record<string, string> = {
  in: 'bg-green-50 text-green-700',
  out: 'bg-red-50 text-red-700',
  adjustment: 'bg-yellow-50 text-yellow-700',
};

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  in: 'In',
  out: 'Out',
  adjustment: 'Adjustment',
};

const SCAN_HISTORY_KEY = 'aura_scan_history';

// ─── Main Component ───────────────────────────────────────────────────────────

export function InventoryPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('stock');
  const [stockRefreshKey, setStockRefreshKey] = useState(0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
        <p className="text-sm text-gray-500">Track stock levels, movements, and batches</p>
      </div>

      {/* Mismanaged Stock Alerts Widget (Task 6) */}
      <MismanagedStockAlerts />

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-[var(--ink-0)] text-[var(--ink-0)]'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'stock' && <StockOverviewTab key={stockRefreshKey} />}
      {activeTab === 'raw_material' && <RawMaterialTab />}
      {activeTab === 'sfg' && <SfgFgTab mode="sfg" />}
      {activeTab === 'fg' && <SfgFgTab mode="fg" />}
      {activeTab === 'replenishment' && <ReplenishmentTab onStockUpdated={() => setStockRefreshKey(k => k + 1)} />}
      {activeTab === 'movements' && <StockMovementsTab onMovementCreated={() => setStockRefreshKey(k => k + 1)} />}
      {activeTab === 'batches' && <BatchTrackingTab />}
      {activeTab === 'scanner' && <BarcodeScannerTab />}
      {activeTab === 'qc' && <QCCheckpointsTab />}
      {activeTab === 'audit' && <StockAuditTab />}
    </div>
  );
}

// ─── Tab 1: Stock Overview ────────────────────────────────────────────────────

function StockOverviewTab() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ total: number; lowStock: number; outOfStock: number }>({
    total: 0,
    lowStock: 0,
    outOfStock: 0,
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);
  // 🔒 2026-09-21 Stock Overview round: filters (spec R7) and supplier In Transit (spec R5).
  const [filters, setFilters] = useState<StockFilterValue>(EMPTY_STOCK_FILTER);
  const [inTransit, setInTransit] = useState<Record<string, number> | null>(null);
  // Only the FIRST load replaces the tab with a spinner. A reload (page / filter change) must not
  // unmount the pivot above, or its own filters and page would reset.
  const [loadedOnce, setLoadedOnce] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/products', { params: { page: String(page), limit: String(limit), ...stockFilterParams(filters) } });
      const list: Product[] = res.data.data?.products ?? res.data.data ?? [];
      setProducts(list);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
      setInTransit(null);
      if (list.length > 0) {
        api.get('/products/in-transit', { params: { ids: list.map((p) => p.id).join(',') } })
          .then((r) => setInTransit(r.data.data ?? {}))
          .catch(() => setInTransit(null)); // unknown shows "-", never a fake 0
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load products');
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [page, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/products/stock-summary');
      const d = res.data.data ?? res.data;
      setSummary({
        total: d?.total ?? 0,
        lowStock: d?.lowStock ?? 0,
        outOfStock: d?.outOfStock ?? 0,
      });
    } catch {
      // keep existing summary on failure
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const getStockStatus = (p: Product) => {
    if (p.stockQty == null) return { label: 'N/A', color: 'bg-gray-100 text-gray-500' };
    if (p.stockQty === 0) return { label: 'Out of Stock', color: 'bg-red-50 text-red-700' };
    if (p.reorderLevel && p.stockQty <= p.reorderLevel)
      return { label: 'Below Reorder', color: 'bg-red-50 text-red-700' };
    return { label: 'In Stock', color: 'bg-green-50 text-green-700' };
  };

  if (loading && !loadedOnce) return <LoadingState text="Loading stock data..." />;
  if (error && !loadedOnce) return <ErrorState text={error} onRetry={fetchProducts} />;

  return (
    <div>
      {/* Summary */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Total Products</p>
          <p className="mt-1 text-lg font-bold text-gray-900">{summary.total}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Low Stock Items</p>
          <p className="mt-1 text-lg font-bold text-red-600">{summary.lowStock}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Out of Stock</p>
          <p className="mt-1 text-lg font-bold text-red-600">{summary.outOfStock}</p>
        </div>
      </div>

      <LocationGradePivot />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-gray-900">Products</p>
        <StockFilters label="Products" value={filters} onChange={(v) => { setPage(1); setFilters(v); }} />
      </div>

      {error ? (
        <ErrorState text={error} onRetry={fetchProducts} />
      ) : products.length === 0 ? (
        <EmptyState icon={Package} text="No products found" sub={filters.category || filters.productType ? 'No products match these filters' : 'Add products to track inventory'} />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">SKU</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Warehouse</th>
                  <th className="px-4 py-3 text-right">Stock (book)</th>
                  <th className="px-4 py-3 text-right">Reorder Level</th>
                  <th className="px-4 py-3 text-right">In Transit</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map((p) => {
                  const status = getStockStatus(p);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{p.sku}</td>
                      <td className="px-4 py-3 text-gray-600">{p.category || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          (p as any).productType === 'Traded' ? 'bg-blue-50 text-blue-700' :
                          (p as any).productType === 'Manufactured' ? 'bg-purple-50 text-purple-700' :
                          'bg-gray-50 text-gray-600'
                        }`}>
                          {(p as any).productType === 'Traded' ? 'WH-1 (Traded)' :
                           (p as any).productType === 'Manufactured' ? 'WH-2 (Manufactured)' :
                           'Main (RM)'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {p.stockQty != null ? `${p.stockQty} ${p.unit || 'pcs'}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {p.reorderLevel != null ? `${p.reorderLevel} ${p.unit || 'pcs'}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {inTransit && inTransit[p.id] != null ? `${inTransit[p.id]} ${p.unit || 'pcs'}` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 2: Stock Movements ───────────────────────────────────────────────────

function StockMovementsTab({ onMovementCreated }: { onMovementCreated: () => void }) {
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    productId: '',
    type: 'in' as 'in' | 'out' | 'adjustment',
    quantity: '',
    reference: '',
    reason: '',
  });

  const fetchMovements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [movRes, prodRes] = await Promise.all([
        api.get('/warehouse/stock-movements').catch(() => ({ data: { data: [] } })),
        api.get('/products'),
      ]);
      setMovements(movRes.data.data?.movements ?? movRes.data.data ?? []);
      setProducts(prodRes.data.data?.products ?? prodRes.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load stock movements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/warehouse/stock-movements', {
        productId: form.productId,
        type: form.type,
        quantity: parseFloat(form.quantity) || 0,
        reference: form.reference || undefined,
        notes: form.reason || undefined,
      });
      setShowModal(false);
      setForm({ productId: '', type: 'in', quantity: '', reference: '', reason: '' });
      fetchMovements();
      onMovementCreated();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create stock adjustment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState text="Loading stock movements..." />;
  if (error) return <ErrorState text={error} onRetry={fetchMovements} />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Stock Adjustment
        </button>
      </div>

      {movements.length === 0 ? (
        <EmptyState icon={RefreshCw} text="No stock movements" sub="Record a stock adjustment to get started" />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Quantity</th>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {movements.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-600">{formatDate(m.date || m.createdAt || '')}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{m.productName || m.product?.name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${MOVEMENT_TYPE_COLORS[m.type] || 'bg-gray-100 text-gray-600'}`}>
                        {m.type === 'in' && <ArrowDownCircle size={12} />}
                        {m.type === 'out' && <ArrowUpCircle size={12} />}
                        {m.type === 'adjustment' && <RefreshCw size={12} />}
                        {MOVEMENT_TYPE_LABELS[m.type] || m.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{m.quantity}</td>
                    <td className="px-4 py-3 text-gray-600">{m.reference || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{m.reason || m.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Stock Adjustment</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product *</label>
                <select
                  value={form.productId}
                  onChange={(e) => setForm({ ...form, productId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Type *</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as any })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="in">Stock In</option>
                    <option value="out">Stock Out</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Quantity *</label>
                  <input
                    type="number"
                    min={form.type === 'adjustment' ? undefined : 1}
                    step={1}
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    placeholder={form.type === 'adjustment' ? 'e.g. -2 or +5' : '0'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reference</label>
                <input
                  type="text"
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="e.g. Production Batch B-001, Order PWC-001"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reason / Notes</label>
                <textarea
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  rows={2}
                  placeholder={form.type === 'adjustment' ? 'e.g. Damaged in transit (use negative qty like -2 to reduce stock)' : ''}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.productId || !form.quantity}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Record Adjustment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 3: Batch Tracking ────────────────────────────────────────────────────

function BatchTrackingTab() {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bForm, setBForm] = useState({
    productId: '', batchNumber: '', totalQty: '', mfgDate: '', expiryDate: '', costPrice: '',
  });

  const handleCreateBatch = async () => {
    try {
      setSaving(true);
      await api.post('/warehouse/batches', {
        productId: bForm.productId,
        batchNumber: bForm.batchNumber,
        totalQty: parseFloat(bForm.totalQty) || 0,
        mfgDate: bForm.mfgDate || undefined,
        expiryDate: bForm.expiryDate || undefined,
        costPrice: bForm.costPrice ? parseFloat(bForm.costPrice) : 0,
      });
      setShowModal(false);
      setBForm({ productId: '', batchNumber: '', totalQty: '', mfgDate: '', expiryDate: '', costPrice: '' });
      fetchBatches();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create batch');
    } finally {
      setSaving(false);
    }
  };

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/warehouse/batches');
      setBatches(res.data.data?.batches ?? res.data.data ?? []);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setBatches([]);
        setError(null);
      } else {
        setError(err.response?.data?.error || 'Failed to load batches');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
    api.get('/products').then(res => setProducts(res.data.data?.products ?? res.data.data ?? [])).catch(() => {});
  }, [fetchBatches]);

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      active: 'bg-green-50 text-green-700',
      expired: 'bg-red-50 text-red-700',
      recalled: 'bg-yellow-50 text-yellow-700',
      depleted: 'bg-gray-100 text-gray-600',
    };
    return map[status.toLowerCase()] || 'bg-gray-100 text-gray-600';
  };

  if (loading) return <LoadingState text="Loading batch data..." />;
  if (error) return <ErrorState text={error} onRetry={fetchBatches} />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Batch
        </button>
      </div>

      {batches.length === 0 ? (
        <EmptyState icon={Boxes} text="No batches found" sub="Batch tracking data will appear here" />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Batch #</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Mfg Date</th>
                  <th className="px-4 py-3">Expiry</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {batches.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{b.batchNumber}</td>
                    <td className="px-4 py-3 text-gray-900">{b.productName || b.product?.name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{(b.manufacturingDate || b.mfgDate) ? formatDate(b.manufacturingDate || b.mfgDate || '') : '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.expiryDate ? formatDate(b.expiryDate) : '-'}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{b.remainingQty ?? b.totalQty ?? b.quantity ?? 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadge(b.status)}`}>
                        {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Batch Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Create Batch</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product *</label>
                <select value={bForm.productId} onChange={(e) => setBForm({ ...bForm, productId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  <option value="">Select product</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Batch Number *</label>
                  <input type="text" value={bForm.batchNumber} onChange={(e) => setBForm({ ...bForm, batchNumber: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Total Quantity *</label>
                  <input type="number" min={1} value={bForm.totalQty} onChange={(e) => setBForm({ ...bForm, totalQty: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Mfg Date</label>
                  <input type="date" value={bForm.mfgDate} onChange={(e) => setBForm({ ...bForm, mfgDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Expiry Date</label>
                  <input type="date" value={bForm.expiryDate} onChange={(e) => setBForm({ ...bForm, expiryDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Cost Price</label>
                <input type="number" min={0} step="0.01" value={bForm.costPrice} onChange={(e) => setBForm({ ...bForm, costPrice: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreateBatch} disabled={saving || !bForm.productId || !bForm.batchNumber || !bForm.totalQty}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Batch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 4: Barcode Scanner ───────────────────────────────────────────────────

function BarcodeScannerTab() {
  const [barcode, setBarcode] = useState('');
  const [lookupProduct, setLookupProduct] = useState<Product | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCustomQty, setShowCustomQty] = useState(false);
  const [customQty, setCustomQty] = useState('1');
  const [customType, setCustomType] = useState<'in' | 'out'>('in');
  const [scanHistory, setScanHistory] = useState<ScanHistoryEntry[]>(() => {
    try {
      const stored = localStorage.getItem(SCAN_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus barcode input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const saveScanHistory = (entries: ScanHistoryEntry[]) => {
    const trimmed = entries.slice(0, 20);
    setScanHistory(trimmed);
    try { localStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(trimmed)); } catch { /* ignore */ }
  };

  const addToHistory = (entry: ScanHistoryEntry) => {
    saveScanHistory([entry, ...scanHistory]);
  };

  const handleLookup = async (code: string) => {
    if (!code.trim()) return;
    try {
      setLookupLoading(true);
      setLookupError(null);
      setLookupProduct(null);
      setShowCustomQty(false);
      const res = await api.get(`/products/lookup/${encodeURIComponent(code.trim())}`);
      setLookupProduct(res.data.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setLookupError('Product not found for this barcode/SKU');
      } else {
        setLookupError(err.response?.data?.error || 'Lookup failed');
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookup(barcode);
    }
  };

  const doStockMovement = async (type: 'in' | 'out', qty: number) => {
    if (!lookupProduct) return;
    try {
      setActionLoading(true);
      await api.post('/warehouse/stock-movements', {
        productId: lookupProduct.id,
        type,
        quantity: qty,
        notes: `Barcode scan: ${barcode}`,
      });
      addToHistory({
        time: new Date().toISOString(),
        barcode: barcode.trim(),
        productName: lookupProduct.name,
        action: type === 'in' ? 'Stock In' : 'Stock Out',
        qty,
      });
      // Refresh product data
      await handleLookup(barcode);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Stock movement failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getProductStock = (p: Product): number => {
    if (p.inventory && p.inventory.length > 0) {
      return p.inventory.reduce((sum, inv) => sum + Number(inv.quantity), 0);
    }
    return (p as any).stockQty ?? 0;
  };

  const getProductImage = (p: Product): string | null => {
    if (p.imageUrl) return p.imageUrl;
    if (p.images && p.images.length > 0) return p.images[0];
    return null;
  };

  return (
    <div>
      {/* Barcode Input */}
      <div className="mb-6">
        <div className="relative mx-auto max-w-xl">
          <ScanBarcode size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scan or type barcode / SKU..."
            autoFocus
            className="w-full rounded-xl border-2 border-gray-300 bg-white py-4 pl-12 pr-4 text-lg font-medium focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] transition-colors"
          />
          {barcode && (
            <button
              onClick={() => { setBarcode(''); setLookupProduct(null); setLookupError(null); inputRef.current?.focus(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
        <p className="mt-1 text-center text-xs text-gray-400">Press Enter to search. Works with hardware barcode scanners.</p>
      </div>

      {/* Loading */}
      {lookupLoading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Looking up product...</span>
        </div>
      )}

      {/* Error */}
      {lookupError && !lookupLoading && (
        <div className="mx-auto max-w-xl rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertTriangle size={32} className="mx-auto mb-2 text-red-400" />
          <p className="text-sm font-medium text-red-700">{lookupError}</p>
          <p className="mt-1 text-xs text-red-500">Scanned: {barcode}</p>
        </div>
      )}

      {/* Product Card */}
      {lookupProduct && !lookupLoading && (
        <div className="mx-auto max-w-xl">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex gap-4">
              {/* Product Image */}
              <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50">
                {getProductImage(lookupProduct) ? (
                  <img src={getProductImage(lookupProduct)!} alt={lookupProduct.name} className="h-full w-full rounded-lg object-contain" />
                ) : (
                  <Package size={32} className="text-gray-300" />
                )}
              </div>
              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">{lookupProduct.name}</h3>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 font-mono">{lookupProduct.sku}</span>
                  {lookupProduct.category && (
                    <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-blue-600">{lookupProduct.category}</span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-gray-500">Current Stock:</span>
                  <span className="text-lg font-bold text-gray-900">{getProductStock(lookupProduct)} {lookupProduct.unit || 'pcs'}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-5 border-t border-gray-100 pt-4">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-400">Quick Actions</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => doStockMovement('in', 1)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  <ArrowDown size={14} />
                  Stock In (+1)
                </button>
                <button
                  onClick={() => doStockMovement('out', 1)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <ArrowUp size={14} />
                  Stock Out (-1)
                </button>
                <button
                  onClick={() => setShowCustomQty(!showCustomQty)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Hash size={14} />
                  Custom Qty
                </button>
              </div>

              {/* Custom Quantity Panel */}
              {showCustomQty && (
                <div className="mt-3 flex items-end gap-3 rounded-lg bg-gray-50 p-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Type</label>
                    <select
                      value={customType}
                      onChange={(e) => setCustomType(e.target.value as 'in' | 'out')}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    >
                      <option value="in">Stock In</option>
                      <option value="out">Stock Out</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Quantity</label>
                    <input
                      type="number"
                      min={1}
                      value={customQty}
                      onChange={(e) => setCustomQty(e.target.value)}
                      className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    />
                  </div>
                  <button
                    onClick={() => {
                      const qty = parseInt(customQty);
                      if (qty > 0) doStockMovement(customType, qty);
                    }}
                    disabled={actionLoading || !customQty || parseInt(customQty) <= 0}
                    className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50 transition-colors"
                  >
                    {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    Apply
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scan History */}
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Scan History</h3>
          {scanHistory.length > 0 && (
            <button
              onClick={() => saveScanHistory([])}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              Clear History
            </button>
          )}
        </div>
        {scanHistory.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">No scan history yet. Scan a barcode to get started.</p>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Barcode</th>
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {scanHistory.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {new Date(entry.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{entry.barcode}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{entry.productName}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          entry.action === 'Stock In' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {entry.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{entry.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 5: QC Checkpoints ───────────────────────────────────────────────────

interface QcBatch {
  id: string;
  batchNumber: string;
  productId: string;
  productName: string;
  productSku: string;
  totalQty: number;
  currentState: string;
  lastTransitionAt: string | null;
  inspectorName: string | null;
}

interface QcTransition {
  id: string;
  productName: string;
  productSku: string;
  batchId: string;
  fromState: string;
  toState: string;
  quantity: number;
  reason: string | null;
  createdAt: string;
}

const QC_COLUMNS = [
  { state: 'ready', label: 'Ready', sublabel: 'QC-1 Pending', color: 'border-gray-300', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-700' },
  { state: 'qc_checked', label: 'QC-1 Checked', sublabel: 'Post-PUF', color: 'border-blue-400', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700' },
  { state: 'fully_packed', label: 'QC-2 Packed', sublabel: 'Stock-in done', color: 'border-green-400', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700' },
  { state: 'dispatched', label: 'QC-3 Dispatched', sublabel: 'Final clearance', color: 'border-emerald-500', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700' },
] as const;

const QC_STATE_LABELS: Record<string, string> = {
  ready: 'Ready',
  qc_checked: 'QC-1 Checked',
  fully_packed: 'QC-2 Packed',
  dispatched: 'QC-3 Dispatched',
  damaged: 'Damaged',
  returned: 'Returned',
};

const NEXT_STATE: Record<string, string> = {
  ready: 'qc_checked',
  qc_checked: 'fully_packed',
  fully_packed: 'dispatched',
};

function QCCheckpointsTab() {
  const [batches, setBatches] = useState<QcBatch[]>([]);
  const [stateCounts, setStateCounts] = useState<Record<string, number>>({});
  const [recentTransitions, setRecentTransitions] = useState<QcTransition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<string | null>(null);

  // Inspector modal state
  const [inspectorModal, setInspectorModal] = useState<{ batchId: string; fromState: string } | null>(null);
  const [inspectorName, setInspectorName] = useState('');

  // Reject modal state
  const [rejectModal, setRejectModal] = useState<{ batchId: string; fromState: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchQcStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/warehouse/qc-status');
      const d = res.data.data;
      setBatches(d.batches || []);
      setStateCounts(d.stateCounts || {});
      setRecentTransitions(d.recentTransitions || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load QC status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQcStatus(); }, [fetchQcStatus]);

  const doTransition = async (batchId: string, fromState: string, toState: string, inspName?: string, notes?: string) => {
    try {
      setTransitioning(batchId);
      await api.post('/warehouse/qc-transition', {
        batchId,
        fromState,
        toState,
        inspectorName: inspName || undefined,
        notes: notes || undefined,
      });
      await fetchQcStatus();
    } catch (err: any) {
      alert(err.response?.data?.error || 'QC transition failed');
    } finally {
      setTransitioning(null);
    }
  };

  const handlePassToNext = (batch: QcBatch) => {
    const next = NEXT_STATE[batch.currentState];
    if (!next) return;
    // If moving to dispatched, require inspector name
    if (next === 'dispatched') {
      setInspectorModal({ batchId: batch.id, fromState: batch.currentState });
      return;
    }
    doTransition(batch.id, batch.currentState, next);
  };

  const handleDispatchConfirm = () => {
    if (!inspectorModal || !inspectorName.trim()) return;
    doTransition(inspectorModal.batchId, inspectorModal.fromState, 'dispatched', inspectorName.trim());
    setInspectorModal(null);
    setInspectorName('');
  };

  const handleReject = (batch: QcBatch) => {
    setRejectModal({ batchId: batch.id, fromState: batch.currentState });
  };

  const handleRejectConfirm = () => {
    if (!rejectModal) return;
    doTransition(rejectModal.batchId, rejectModal.fromState, 'damaged', undefined, rejectReason.trim() || 'Rejected during QC');
    setRejectModal(null);
    setRejectReason('');
  };

  const getTimeInState = (lastTransitionAt: string | null): string => {
    if (!lastTransitionAt) return 'New';
    const diff = Date.now() - new Date(lastTransitionAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (loading) return <LoadingState text="Loading QC data..." />;
  if (error) return <ErrorState text={error} onRetry={fetchQcStatus} />;

  // Also show damaged/returned counts
  const damagedCount = stateCounts.damaged || 0;
  const returnedCount = stateCounts.returned || 0;

  return (
    <div>
      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {QC_COLUMNS.map((col) => (
          <div key={col.state} className={`rounded-xl border-l-4 ${col.color} bg-white p-4 shadow-sm`}>
            <p className="text-xs font-medium text-gray-500">{col.label}</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{stateCounts[col.state] || 0}</p>
          </div>
        ))}
        {damagedCount > 0 && (
          <div className="rounded-xl border-l-4 border-red-400 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500">Damaged</p>
            <p className="mt-1 text-2xl font-bold text-red-600">{damagedCount}</p>
          </div>
        )}
        {returnedCount > 0 && (
          <div className="rounded-xl border-l-4 border-orange-400 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500">Returned</p>
            <p className="mt-1 text-2xl font-bold text-orange-600">{returnedCount}</p>
          </div>
        )}
      </div>

      {/* QC Pipeline Kanban */}
      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {QC_COLUMNS.map((col) => {
          const columnBatches = batches.filter((b) => b.currentState === col.state);
          return (
            <div key={col.state} className={`rounded-xl border ${col.color} ${col.bg} p-4`}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">{col.label}</h3>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-bold ${col.badge}`}>
                  {columnBatches.length}
                </span>
              </div>
              <p className="mb-3 text-[10px] uppercase tracking-wider text-gray-400">{col.sublabel}</p>

              {columnBatches.length === 0 ? (
                <p className="py-6 text-center text-xs text-gray-400">No batches</p>
              ) : (
                <div className="space-y-2">
                  {columnBatches.map((batch) => (
                    <div key={batch.id} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-900">{batch.productName}</p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            Batch: <span className="font-mono font-medium text-[var(--ink-0)]">{batch.batchNumber}</span>
                          </p>
                        </div>
                        <span className="ml-2 inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-bold text-gray-700">
                          {batch.totalQty}
                        </span>
                      </div>

                      <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-400">
                        <Clock size={10} />
                        <span>{getTimeInState(batch.lastTransitionAt)}</span>
                        {batch.inspectorName && (
                          <>
                            <User size={10} className="ml-1" />
                            <span>{batch.inspectorName}</span>
                          </>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {NEXT_STATE[col.state] && (
                        <div className="mt-2 flex gap-1.5">
                          <button
                            onClick={() => handlePassToNext(batch)}
                            disabled={transitioning === batch.id}
                            className="flex flex-1 items-center justify-center gap-1 rounded-md bg-[var(--ink-0)] px-2 py-1.5 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50 transition-colors"
                          >
                            {transitioning === batch.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <ChevronRight size={12} />
                            )}
                            Pass
                          </button>
                          <button
                            onClick={() => handleReject(batch)}
                            disabled={transitioning === batch.id}
                            className="flex items-center justify-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors"
                          >
                            <XCircle size={12} />
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recent Transitions */}
      {recentTransitions.length > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Recent QC Transitions</h3>
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">From</th>
                    <th className="px-4 py-3">To</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                    <th className="px-4 py-3">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recentTransitions.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">{formatDate(t.createdAt)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{t.productName}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {QC_STATE_LABELS[t.fromState || ''] || t.fromState}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          t.toState === 'damaged' ? 'bg-red-100 text-red-700'
                          : t.toState === 'dispatched' ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-blue-100 text-blue-700'
                        }`}>
                          {QC_STATE_LABELS[t.toState || ''] || t.toState}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{t.quantity}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{t.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Inspector Name Modal (for QC-3 dispatch) */}
      {inspectorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setInspectorModal(null); setInspectorName(''); }}>
          <div className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Dispatch Clearance (QC-3)</h2>
              <button onClick={() => { setInspectorModal(null); setInspectorName(''); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Inspector Name *</label>
                <input
                  type="text"
                  value={inspectorName}
                  onChange={(e) => setInspectorName(e.target.value)}
                  placeholder="e.g. Randhir Sharma"
                  autoFocus
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <p className="text-xs text-gray-400">Inspector name will be recorded for final dispatch clearance.</p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => { setInspectorModal(null); setInspectorName(''); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleDispatchConfirm}
                disabled={!inspectorName.trim()}
                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircle size={14} />
                Confirm Dispatch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setRejectModal(null); setRejectReason(''); }}>
          <div className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Reject Batch</h2>
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reason for Rejection</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder="Describe the issue (e.g. foam density failed, packaging damage)..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                <XCircle size={14} />
                Mark as Damaged
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Stock Audit (Blind Mode) ──────────────────────────────────────────

interface AuditData {
  auditId: string;
  name: string;
  type: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  counts: Array<{ productId: string; productName: string; physicalCount: number; location: string | null; countedAt: string }>;
  results?: {
    items: Array<{ productId: string; productName: string; productSku: string; systemQty: number; physicalQty: number; variance: number; variancePercent: number; status: string }>;
    summary: { totalCounted: number; matched: number; flagged: number };
  } | null;
}

function StockAuditTab() {
  const [audits, setAudits] = useState<AuditData[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeAudit, setActiveAudit] = useState<AuditData | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [blindMode, setBlindMode] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [countForm, setCountForm] = useState({ productId: '', physicalCount: '', location: '' });

  const fetchAudits = useCallback(async () => {
    try {
      setLoading(true);
      const [audRes, prodRes] = await Promise.all([
        api.get('/warehouse/stock-audit'),
        api.get('/products'),
      ]);
      setAudits(audRes.data.data ?? []);
      setProducts(prodRes.data.data?.products ?? prodRes.data.data ?? []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAudits(); }, [fetchAudits]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      setCreating(true);
      const res = await api.post('/warehouse/stock-audit', { name: newName, type: blindMode ? 'blind' : 'standard' });
      setActiveAudit(res.data.data);
      setShowCreate(false);
      setNewName('');
      fetchAudits();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create audit');
    } finally { setCreating(false); }
  };

  const handleSubmitCount = async () => {
    if (!activeAudit || !countForm.productId || !countForm.physicalCount) return;
    try {
      setSubmitting(true);
      await api.post(`/warehouse/stock-audit/${activeAudit.auditId}/count`, {
        productId: countForm.productId,
        physicalCount: parseInt(countForm.physicalCount) || 0,
        location: countForm.location || null,
      });
      // Refresh audit
      const res = await api.get(`/warehouse/stock-audit/${activeAudit.auditId}`);
      setActiveAudit(res.data.data);
      setCountForm({ productId: '', physicalCount: '', location: '' });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to submit count');
    } finally { setSubmitting(false); }
  };

  const handleComplete = async () => {
    if (!activeAudit) return;
    try {
      setCompleting(true);
      const res = await api.post(`/warehouse/stock-audit/${activeAudit.auditId}/complete`);
      setActiveAudit(res.data.data);
      fetchAudits();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to complete audit');
    } finally { setCompleting(false); }
  };

  const handleApprove = async () => {
    if (!activeAudit) return;
    try {
      setApproving(true);
      const res = await api.post(`/warehouse/stock-audit/${activeAudit.auditId}/approve`);
      setActiveAudit(res.data.data);
      fetchAudits();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve audit');
    } finally { setApproving(false); }
  };

  if (loading) return <LoadingState text="Loading audit data..." />;

  // Detail view
  if (activeAudit) {
    const isInProgress = activeAudit.status === 'in_progress';
    const isCompleted = activeAudit.status === 'completed';
    const isBlind = activeAudit.type === 'blind';

    return (
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setActiveAudit(null)} className="text-sm text-[var(--ink-0)] hover:underline">&larr; All Audits</button>
            <h3 className="text-lg font-semibold text-gray-900">{activeAudit.name}</h3>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
              activeAudit.status === 'in_progress' ? 'bg-yellow-50 text-yellow-700' :
              activeAudit.status === 'completed' ? 'bg-blue-50 text-blue-700' :
              activeAudit.status === 'approved' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>{activeAudit.status.replace('_', ' ').toUpperCase()}</span>
            {isBlind && <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700"><EyeOff size={12} /> Blind</span>}
          </div>
          <div className="flex gap-2">
            {isInProgress && (
              <button onClick={handleComplete} disabled={completing || activeAudit.counts.length === 0}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {completing ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />}
                Complete Audit
              </button>
            )}
            {isCompleted && (
              <button onClick={handleApprove} disabled={approving}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                {approving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Approve Audit
              </button>
            )}
          </div>
        </div>

        {/* Count Entry */}
        {isInProgress && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
            <h4 className="mb-3 text-sm font-semibold text-gray-700">Submit Physical Count</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Product *</label>
                <select value={countForm.productId} onChange={(e) => setCountForm({ ...countForm, productId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  <option value="">Select product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Physical Count *</label>
                <input type="number" min={0} value={countForm.physicalCount}
                  onChange={(e) => setCountForm({ ...countForm, physicalCount: e.target.value })}
                  placeholder="0"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Location</label>
                <input type="text" value={countForm.location}
                  onChange={(e) => setCountForm({ ...countForm, location: e.target.value })}
                  placeholder="e.g., Rack A3"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="flex items-end">
                <button onClick={handleSubmitCount}
                  disabled={submitting || !countForm.productId || !countForm.physicalCount}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Submit Count
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submitted Counts */}
        {isInProgress && activeAudit.counts.length > 0 && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <h4 className="text-sm font-semibold text-gray-700">Submitted Counts ({activeAudit.counts.length})</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2">Product</th>
                    <th className="px-4 py-2 text-right">Physical Count</th>
                    <th className="px-4 py-2">Location</th>
                    <th className="px-4 py-2">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {activeAudit.counts.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{c.productName}</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">{c.physicalCount}</td>
                      <td className="px-4 py-2 text-gray-600">{c.location || '-'}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{new Date(c.countedAt).toLocaleTimeString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Variance Results */}
        {activeAudit.results && (
          <div>
            {/* Summary Cards */}
            <div className="mb-4 grid grid-cols-3 gap-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-xs text-gray-500">Total Items Counted</p>
                <p className="mt-1 text-lg font-bold text-gray-900">{activeAudit.results.summary.totalCounted}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-xs text-gray-500">Items Matched</p>
                <p className="mt-1 text-lg font-bold text-green-600">{activeAudit.results.summary.matched}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-xs text-gray-500">Items with Variance</p>
                <p className="mt-1 text-lg font-bold text-red-600">{activeAudit.results.summary.flagged}</p>
              </div>
            </div>

            {/* Variance Table */}
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                <h4 className="text-sm font-semibold text-gray-700">Variance Report</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2">Product</th>
                      <th className="px-4 py-2">SKU</th>
                      <th className="px-4 py-2 text-right">System Qty</th>
                      <th className="px-4 py-2 text-right">Physical Qty</th>
                      <th className="px-4 py-2 text-right">Variance</th>
                      <th className="px-4 py-2 text-right">Variance %</th>
                      <th className="px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeAudit.results.items.map((r, i) => (
                      <tr key={i} className={`hover:bg-gray-50 ${r.status === 'flagged' ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-2 font-medium text-gray-900">{r.productName}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-600">{r.productSku || '-'}</td>
                        <td className="px-4 py-2 text-right text-gray-900">{r.systemQty}</td>
                        <td className="px-4 py-2 text-right text-gray-900">{r.physicalQty}</td>
                        <td className={`px-4 py-2 text-right font-medium ${r.variance > 0 ? 'text-green-600' : r.variance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {r.variance > 0 ? '+' : ''}{r.variance}
                        </td>
                        <td className={`px-4 py-2 text-right ${r.status === 'flagged' ? 'font-bold text-red-600' : 'text-gray-600'}`}>
                          {r.variancePercent.toFixed(2)}%
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            r.status === 'flagged' ? 'bg-red-100 text-red-700' : 'bg-green-50 text-green-700'
                          }`}>
                            {r.status === 'flagged' ? 'FLAGGED' : 'OK'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Audit list view
  return (
    <div>
      <div className="mb-4 flex items-center justify-end">
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]">
          <Plus size={16} />
          Start Audit
        </button>
      </div>

      {audits.length === 0 ? (
        <EmptyState icon={ClipboardList} text="No stock audits" sub="Start a surprise audit to verify physical inventory" />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Audit ID</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Items Counted</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {audits.map((a, i) => (
                  <tr key={i} className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setActiveAudit(a)}>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--ink-0)]">{a.auditId}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{a.name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        a.type === 'blind' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {a.type === 'blind' && <EyeOff size={10} />} {a.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{a.counts?.length || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        a.status === 'in_progress' ? 'bg-yellow-50 text-yellow-700' :
                        a.status === 'completed' ? 'bg-blue-50 text-blue-700' :
                        a.status === 'approved' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>{a.status.replace('_', ' ')}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Audit Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreate(false)}>
          <div className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Start Stock Audit</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Audit Name *</label>
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g., Monthly Audit - March 2026"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="blind-mode" checked={blindMode} onChange={(e) => setBlindMode(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]" />
                <label htmlFor="blind-mode" className="flex items-center gap-2 text-sm text-gray-700">
                  <EyeOff size={14} /> Blind Mode
                  <span className="text-xs text-gray-500">(hides system quantities during counting)</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button onClick={() => setShowCreate(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !newName.trim()}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {creating && <Loader2 size={14} className="animate-spin" />}
                Start Audit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 2: Replenishment ─────────────────────────────────────────────────────

// Matches GET /replenishment/monitor → data.belowMinimum[] / data.healthy[]
interface MonitorItem {
  inventoryId: string;
  productId: string;
  productName: string;
  productSku: string;
  productType: string;
  unit: string;
  warehouse: string;
  currentQty: number;
  minLevel: number;
  maxLevel: number;
  reorderLevel: number;
  fixedVendorId: string | null;
  status: 'below_minimum' | 'healthy';
  deficit: number;
  replenishNeeded: number;
}

interface MonitorSummary {
  total: number;
  belowMinimum: number;
  healthy: number;
  criticalCount: number;
}

// Matches GET /replenishment/history
interface ReplenishmentHistoryEntry {
  id: string;
  key: string;
  action: string;
  referenceId?: string | null;
  reference?: string | null;
  message?: string;
  productId?: string;
  productName?: string;
  productSku?: string;
  currentQty?: number;
  replenishQty?: number;
  warehouse?: string;
  triggerType?: string;
  triggeredBy?: string;
  ts?: string;
  // GRN inspection logs
  grnNumber?: string;
  purchaseOrderId?: string;
  poNumber?: string;
  inspectorName?: string;
  itemsProcessed?: number;
  stockUpdated?: number;
  rejectionCount?: number;
}

interface GrnRecord {
  grnNumber: string;
  productName: string;
  vendorName?: string;
  receivedQty: number;
  acceptedQty: number;
  rejectedQty: number;
  inspectionStatus: string;
  stockUpdated: boolean;
  createdAt: string;
}

// Matches POST /replenishment/grn-inspect → response
interface GrnInspectResult {
  grnNumber: string;
  itemsProcessed: number;
  stockUpdated: number;
  rejections: { productId: string; rejectedQty: number; reason: string }[];
}

// For the PO selector in the GRN form
interface PurchaseOrderOption {
  id: string;
  number: string;
  vendorName?: string;
  status: string;
  items?: { productId: string; description?: string; quantity: number }[];
}

interface Vendor {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
}

// For backwards-compat with the settings modal and stock-level table
interface StockLevel {
  id: string;          // inventoryId
  productId: string;
  productName: string;
  productSku: string | null;
  productType: string | null;
  unit: string | null;
  warehouse: string;
  quantity: number;     // currentQty
  minStock: number | null;  // minLevel
  maxStock: number | null;  // maxLevel
  reorderLevel: number;
  fixedVendorId: string | null;
  status: 'ok' | 'low' | 'critical';
  orderType: string;   // derived: fixedVendorId ? 'standard' : 'custom' (kept for UI)
}

// For the alerts view
interface ReplenishmentItem {
  id: string;
  productId: string;
  productName: string;
  productSku: string | null;
  productType: string | null;
  unit: string | null;
  warehouse: string;
  quantity: number;
  minStock: number;
  maxStock: number | null;
  shortage: number;
  replenishNeeded: number;
  orderType: string;
  fixedVendorId: string | null;
  fixedVendor: { id: string; name: string } | null;
}

function StockLevelBar({ quantity, min, max }: { quantity: number; min: number | null; max: number | null }) {
  if (!max || max === 0) return <span className="text-xs text-[var(--text-muted)]">No max set</span>;
  const pct = Math.min(100, Math.round((quantity / max) * 100));
  const minPct = min ? Math.min(100, Math.round((min / max) * 100)) : 0;
  const color = quantity === 0 ? 'bg-red-500' : min && quantity < min ? 'bg-red-400' : pct < 30 ? 'bg-amber-400' : 'bg-green-400';
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-2 w-28 rounded-full bg-[var(--bg-muted)] overflow-visible">
        {/* Min marker */}
        {minPct > 0 && (
          <div
            className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-orange-500 z-10"
            style={{ left: `${minPct}%` }}
            title={`Min: ${min}`}
          />
        )}
        <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-[var(--text-secondary)] w-8 text-right">{pct}%</span>
    </div>
  );
}

function ReplenishmentTab({ onStockUpdated }: { onStockUpdated: () => void }) {
  const [view, setView] = useState<'levels' | 'alerts' | 'grn' | 'history'>('levels');
  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [replenishItems, setReplenishItems] = useState<ReplenishmentItem[]>([]);
  const [grnHistory, setGrnHistory] = useState<GrnRecord[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [triggeringByProduct, setTriggeringByProduct] = useState<Record<string, boolean>>({});
  const [triggerResultByProduct, setTriggerResultByProduct] = useState<Record<string, string>>({});
  const [customSoByProduct, setCustomSoByProduct] = useState<Record<string, string>>({});
  const [scanningAll, setScanningAll] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);

  // Settings modal
  const [settingsTarget, setSettingsTarget] = useState<StockLevel | null>(null);
  const [settingsForm, setSettingsForm] = useState({ minStock: '', maxStock: '', orderType: 'standard', fixedVendorId: '' });

  // GRN form
  const [grnForm, setGrnForm] = useState({
    productId: '', productName: '', receivedQty: '', inspectedQty: '', rejectedQty: '0',
    receivedBy: '', inspectorName: '', inspectionStatus: 'pass' as 'pass' | 'partial' | 'fail',
    notes: '', poReference: '', vendorId: '', vendorName: '',
  });
  const [grnSuccess, setGrnSuccess] = useState<GrnRecord | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [monitorRes, historyRes, vendorsRes] = await Promise.all([
        api.get('/replenishment/monitor').catch(() => null),
        api.get('/replenishment/history').catch(() => null),
        api.get('/vendors').catch(() => ({ data: { data: { vendors: [] } } })),
      ]);

      if (monitorRes?.data?.data) {
        const monitor = monitorRes.data.data;
        const allRows = [...(monitor.belowMinimum ?? []), ...(monitor.healthy ?? [])];
        const mappedLevels: StockLevel[] = allRows.map((r: any) => ({
          id: r.inventoryId,
          productId: r.productId,
          productName: r.productName,
          productSku: r.productSku ?? null,
          productType: r.productType ?? null,
          unit: r.unit ?? null,
          warehouse: r.warehouse,
          quantity: Number(r.currentQty ?? 0),
          minStock: r.minLevel != null ? Number(r.minLevel) : null,
          maxStock: r.maxLevel != null ? Number(r.maxLevel) : null,
          reorderLevel: Number(r.reorderLevel ?? 0),
          fixedVendorId: r.fixedVendorId ?? null,
          status: Number(r.currentQty ?? 0) === 0
            ? 'critical'
            : Number(r.currentQty ?? 0) <= Number(r.reorderLevel ?? 0) ? 'low' : 'ok',
          orderType: r.orderType || 'standard',
        }));
        setStockLevels(mappedLevels);

        const alerts: ReplenishmentItem[] = (monitor.belowMinimum ?? []).map((r: any) => ({
          id: r.inventoryId,
          productId: r.productId,
          productName: r.productName,
          productSku: r.productSku ?? null,
          productType: r.productType ?? null,
          unit: r.unit ?? null,
          warehouse: r.warehouse,
          quantity: Number(r.currentQty ?? 0),
          minStock: Number(r.minLevel ?? 0),
          maxStock: r.maxLevel != null ? Number(r.maxLevel) : null,
          shortage: Number(r.deficit ?? 0),
          replenishNeeded: Number(r.replenishNeeded ?? 0),
          orderType: r.orderType || 'standard',
          fixedVendorId: r.fixedVendorId ?? null,
          fixedVendor: null,
        }));
        setReplenishItems(alerts);
      } else {
        const [levelsRes, replenishRes] = await Promise.all([
          api.get('/warehouse/inventory/stock-levels').catch(() => ({ data: { data: [] } })),
          api.get('/warehouse/inventory/replenishment').catch(() => ({ data: { data: [] } })),
        ]);
        setStockLevels(levelsRes.data.data ?? []);
        setReplenishItems(replenishRes.data.data ?? []);
      }

      if (historyRes?.data?.data) {
        setGrnHistory((historyRes.data.data ?? []).filter((h: any) => h.action === 'grn_inspection'));
      } else {
        const grnRes = await api.get('/warehouse/inventory/grn').catch(() => ({ data: { data: [] } }));
        setGrnHistory(grnRes.data.data ?? []);
      }

      const vendorData = vendorsRes.data.data?.vendors ?? vendorsRes.data.data ?? [];
      setVendors(vendorData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load replenishment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredLevels = stockLevels.filter((r) => {
    const matchSearch = !search || r.productName.toLowerCase().includes(search.toLowerCase()) || (r.productSku || '').toLowerCase().includes(search.toLowerCase());
    const matchType = !typeFilter || (r.productType || '').toLowerCase().includes(typeFilter.toLowerCase());
    return matchSearch && matchType;
  });

  const openSettings = (item: StockLevel) => {
    setSettingsTarget(item);
    setSettingsForm({
      minStock: item.minStock != null ? String(item.minStock) : '',
      maxStock: item.maxStock != null ? String(item.maxStock) : '',
      orderType: item.orderType || 'standard',
      fixedVendorId: item.fixedVendorId || '',
    });
  };

  const handleSaveSettings = async () => {
    if (!settingsTarget) return;
    try {
      setSaving(true);
      await api.put(`/warehouse/inventory/${settingsTarget.id}/settings`, {
        minStock: settingsForm.minStock ? parseFloat(settingsForm.minStock) : undefined,
        maxStock: settingsForm.maxStock ? parseFloat(settingsForm.maxStock) : undefined,
        orderType: settingsForm.orderType,
        fixedVendorId: settingsForm.fixedVendorId || null,
      });
      setSettingsTarget(null);
      fetchAll();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGrnSubmit = async () => {
    if (!grnForm.productId || !grnForm.receivedQty || !grnForm.receivedBy) {
      alert('Product, received quantity and received by are required');
      return;
    }
    try {
      setSaving(true);
      let grnResult: any = null;
      if (grnForm.poReference && grnForm.inspectorName) {
        const newStatus =
          grnForm.inspectionStatus === 'pass' ? 'passed' :
          grnForm.inspectionStatus === 'partial' ? 'partial' : 'failed';
        const res = await api.post('/replenishment/grn-inspect', {
          purchaseOrderId: grnForm.poReference,
          inspectorName: grnForm.inspectorName,
          items: [{
            productId: grnForm.productId,
            orderedQty: parseFloat(grnForm.receivedQty),
            receivedQty: parseFloat(grnForm.inspectedQty || grnForm.receivedQty),
            inspectionStatus: newStatus,
            rejectedQty: parseFloat(grnForm.rejectedQty || '0'),
            notes: grnForm.notes || null,
          }],
        });
        grnResult = res.data.data;
      } else {
        const res = await api.post('/warehouse/inventory/grn', {
          productId: grnForm.productId,
          productName: grnForm.productName,
          receivedQty: parseFloat(grnForm.receivedQty),
          inspectedQty: parseFloat(grnForm.inspectedQty || grnForm.receivedQty),
          rejectedQty: parseFloat(grnForm.rejectedQty || '0'),
          receivedBy: grnForm.receivedBy,
          inspectorName: grnForm.inspectorName || null,
          inspectionStatus: grnForm.inspectionStatus,
          notes: grnForm.notes || null,
          poReference: grnForm.poReference || null,
          vendorId: grnForm.vendorId || null,
          vendorName: grnForm.vendorName || null,
          updateStock: true,
        });
        grnResult = res.data.data;
      }
      setGrnSuccess(grnResult);
      setGrnForm({ productId: '', productName: '', receivedQty: '', inspectedQty: '', rejectedQty: '0', receivedBy: '', inspectorName: '', inspectionStatus: 'pass', notes: '', poReference: '', vendorId: '', vendorName: '' });
      fetchAll();
      onStockUpdated();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  const handleReplenishmentTrigger = async (
    item: ReplenishmentItem,
    triggerType: '95_percent' | '5_percent',
  ) => {
    if (triggerType === '5_percent' && !(customSoByProduct[item.productId] || '').trim()) {
      alert('Sales Order ID is required for 5% custom trigger.');
      return;
    }
    setTriggeringByProduct((prev) => ({ ...prev, [item.productId]: true }));
    setTriggerResultByProduct((prev) => ({ ...prev, [item.productId]: '' }));
    try {
      const payload: Record<string, unknown> = {
        productId: item.productId,
        triggerType,
      };
      if (triggerType === '5_percent') payload.salesOrderId = customSoByProduct[item.productId].trim();
      const res = await api.post('/replenishment/trigger', payload);
      const data = res.data?.data || {};
      const action = data.action || 'processed';
      const reference = data.reference || '';
      const msg = data.message || '';
      const summary = [action, reference ? `#${reference}` : '', msg].filter(Boolean).join(' — ');
      setTriggerResultByProduct((prev) => ({ ...prev, [item.productId]: summary || 'Replenishment triggered' }));
      fetchAll();
      onStockUpdated();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to trigger replenishment';
      setTriggerResultByProduct((prev) => ({ ...prev, [item.productId]: msg }));
    } finally {
      setTriggeringByProduct((prev) => ({ ...prev, [item.productId]: false }));
    }
  };

  const statusBadge = (status: string) => {
    if (status === 'critical') return <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700"><AlertTriangle size={10} />Critical</span>;
    if (status === 'low') return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700"><TrendingDown size={10} />Low</span>;
    return <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700"><CheckCircle size={10} />OK</span>;
  };

  const orderTypeBadge = (t: string) => t === 'custom'
    ? <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">5% Custom</span>
    : <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">95% Standard</span>;

  const inspectionBadge = (s: string) => {
    if (s === 'pass') return <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"><PackageCheck size={10} />Pass</span>;
    if (s === 'partial') return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"><PackageCheck size={10} />Partial</span>;
    return <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"><PackageX size={10} />Fail</span>;
  };

  if (loading) return <LoadingState text="Loading replenishment data..." />;
  if (error) return <ErrorState text={error} onRetry={fetchAll} />;

  const criticalCount = stockLevels.filter((r) => r.status === 'critical').length;
  const lowCount = stockLevels.filter((r) => r.status === 'low').length;

  return (
    <div>
      {/* KPI bar */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
          <p className="text-xs text-[var(--text-muted)]">Total Products</p>
          <p className="mt-1 text-xl font-bold text-[var(--text-primary)]">{stockLevels.length}</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs text-red-600">Below Minimum</p>
          <p className="mt-1 text-xl font-bold text-red-700">{criticalCount}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs text-amber-600">Low Stock</p>
          <p className="mt-1 text-xl font-bold text-amber-700">{lowCount}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
          <p className="text-xs text-[var(--text-muted)]">GRNs Created</p>
          <p className="mt-1 text-xl font-bold text-[var(--text-primary)]">{grnHistory.length}</p>
        </div>
      </div>

      {/* Scan trigger + View tabs */}
      <div className="mb-5 flex items-center justify-between">
      <div className="flex gap-1 rounded-lg bg-[var(--bg-muted)] p-1 w-fit">
        {([
          { key: 'levels', label: 'Stock Levels', icon: BarChart3 },
          { key: 'alerts', label: `Alerts (${replenishItems.length})`, icon: AlertTriangle },
          { key: 'grn', label: 'GRN Entry', icon: PackageCheck },
          { key: 'history', label: 'GRN History', icon: ClipboardList },
        ] as const).map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              view === v.key
                ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
          >
            <v.icon size={13} />
            {v.label}
          </button>
        ))}
      </div>
      <button
        onClick={async () => {
          setScanningAll(true); setScanResult(null);
          try {
            const res = await api.post('/replenishment/auto-scan');
            const d = res.data?.data;
            setScanResult(`Scanned ${d?.scanned ?? 0} items — ${d?.belowMinimum ?? 0} below min, ${d?.poCreated ?? 0} POs created`);
            fetchAll();
          } catch { setScanResult('Scan failed'); }
          finally { setScanningAll(false); }
        }}
        disabled={scanningAll}
        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {scanningAll ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
        {scanningAll ? 'Scanning...' : 'Trigger Scan Now'}
      </button>
      </div>
      {scanResult && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-xs text-green-700">
          {scanResult}
        </div>
      )}

      {/* ── View: Stock Levels ── */}
      {view === 'levels' && (
        <div>
          {/* Filters */}
          <div className="mb-4 flex flex-wrap gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] pl-8 pr-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] w-56"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
            >
              <option value="">All Types</option>
              <option value="Raw Material">Raw Material</option>
              <option value="Manufactured">Manufactured</option>
              <option value="Traded">Traded</option>
            </select>
          </div>

          {filteredLevels.length === 0 ? (
            <EmptyState icon={Package} text="No inventory records found" sub="Set min/max levels to enable replenishment alerts" />
          ) : (
            <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-muted)] text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                      <th className="px-4 py-3">Product</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3 text-right">Current</th>
                      <th className="px-4 py-3 text-right">Min</th>
                      <th className="px-4 py-3 text-right">Max</th>
                      <th className="px-4 py-3">Level</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Order Rule</th>
                      <th className="px-4 py-3 text-center">Settings</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-primary)]">
                    {filteredLevels.map((r) => (
                      <tr key={r.id} className={`transition-colors hover:bg-[var(--bg-surface-hover)] ${r.status === 'critical' ? 'bg-red-50/30' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-[var(--text-primary)]">{r.productName}</p>
                          <p className="text-xs text-[var(--text-muted)]">{r.productSku || '—'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">{r.productType || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-[var(--text-primary)]">
                          {r.quantity} <span className="text-xs font-normal text-[var(--text-muted)]">{r.unit || 'pcs'}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--text-secondary)]">
                          {r.minStock != null ? r.minStock : <span className="text-[var(--text-muted)]">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--text-secondary)]">
                          {r.maxStock != null ? r.maxStock : <span className="text-[var(--text-muted)]">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <StockLevelBar quantity={r.quantity} min={r.minStock} max={r.maxStock} />
                        </td>
                        <td className="px-4 py-3">{statusBadge(r.status)}</td>
                        <td className="px-4 py-3">{orderTypeBadge(r.orderType)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => openSettings(r)}
                            className="rounded-lg p-1.5 text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]"
                            title="Configure min/max/vendor"
                          >
                            <Settings2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── View: Alerts / Replenishment Queue ── */}
      {view === 'alerts' && (
        <div>
          {replenishItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-green-200 bg-green-50 py-16 text-green-700">
              <CheckCircle size={40} className="mb-2 text-green-400" />
              <p className="font-semibold">All stock levels are healthy</p>
              <p className="text-sm text-green-600">No items are below their minimum threshold</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Flow legend */}
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-muted)] px-4 py-3 text-xs text-[var(--text-secondary)]">
                <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                <span className="font-medium text-[var(--text-primary)]">Replenishment Flow:</span>
                <span>Stock Below Min</span>
                <ChevronRight size={12} />
                <span className="text-blue-600 font-medium">95% Standard</span> <span>= Fixed vendor → Auto PO (Qty = Max − Current)</span>
                <span className="mx-1">|</span>
                <span className="text-purple-600 font-medium">5% Custom</span> <span>= SO-driven exact qty</span>
                <ChevronRight size={12} />
                <span>GRN Inspection</span>
                <ChevronRight size={12} />
                <span>Stock Updated</span>
              </div>

              {replenishItems.map((item) => {
                const isStandard = item.orderType === 'standard';
                const hasFixedVendor = !!item.fixedVendor;
                const replenishQty = item.maxStock != null ? item.maxStock - item.quantity : null;
                const isTriggering = !!triggeringByProduct[item.productId];
                const triggerResult = triggerResultByProduct[item.productId];

                return (
                  <div
                    key={item.id}
                    className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4"
                  >
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-[var(--text-primary)]">{item.productName}</p>
                          <span className="text-xs text-[var(--text-muted)]">{item.productSku}</span>
                          {statusBadge('critical')}
                          {orderTypeBadge(item.orderType)}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm text-[var(--text-secondary)]">
                          <span>Current: <strong className="text-red-600">{item.quantity}</strong> {item.unit || 'pcs'}</span>
                          <span>Min: <strong>{item.minStock}</strong></span>
                          {item.maxStock && <span>Max: <strong>{item.maxStock}</strong></span>}
                          <span>Shortage: <strong className="text-red-600">{item.shortage}</strong></span>
                          {replenishQty && <span>Order Qty: <strong className="text-[var(--ink-0)]">{replenishQty}</strong> {item.unit || 'pcs'}</span>}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 items-end text-xs">
                        {isStandard ? (
                          hasFixedVendor ? (
                            <div className="text-right">
                              <p className="text-[var(--text-muted)]">Fixed Vendor</p>
                              <p className="font-semibold text-[var(--text-primary)]">{item.fixedVendor!.name}</p>
                              <button
                                onClick={() => handleReplenishmentTrigger(item, '95_percent')}
                                disabled={isTriggering}
                                className="mt-1 flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                                title="Run 95% min/max replenishment trigger"
                              >
                                {isTriggering ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
                                Trigger 95% Auto-PO
                              </button>
                              <button
                                onClick={() => {
                                  setView('grn');
                                  setGrnForm((f) => ({
                                    ...f,
                                    productId: item.productId,
                                    productName: item.productName,
                                    vendorId: item.fixedVendorId || '',
                                    vendorName: item.fixedVendor?.name || '',
                                    receivedQty: replenishQty != null ? String(replenishQty) : '',
                                    inspectedQty: replenishQty != null ? String(replenishQty) : '',
                                  }));
                                }}
                                className="mt-1 flex items-center gap-1 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-white hover:bg-[var(--ink-1)] transition-colors"
                              >
                                <ShoppingCart size={12} />
                                Log GRN
                              </button>
                            </div>
                          ) : (
                            <div className="text-right">
                              <p className="text-amber-600 font-medium">No fixed vendor</p>
                              <p className="text-[var(--text-muted)]">Send quotation to multiple vendors</p>
                              <button
                                onClick={() => handleReplenishmentTrigger(item, '95_percent')}
                                disabled={isTriggering}
                                className="mt-1 flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition-colors"
                                title="Run 95% min/max trigger (quotation flow expected)"
                              >
                                {isTriggering ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
                                Trigger 95% Quotation Flow
                              </button>
                              <button
                                onClick={() => openSettings(stockLevels.find((s) => s.id === item.id)!)}
                                className="mt-1 flex items-center gap-1 rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] transition-colors"
                              >
                                <Settings2 size={12} />
                                Set Vendor
                              </button>
                            </div>
                          )
                        ) : (
                          <div className="text-right">
                            <p className="text-purple-600 font-medium">SO-Driven (Custom)</p>
                            <p className="text-[var(--text-muted)]">Order exact qty per Sales Order</p>
                            <input
                              type="text"
                              value={customSoByProduct[item.productId] || ''}
                              onChange={(e) => setCustomSoByProduct((prev) => ({ ...prev, [item.productId]: e.target.value }))}
                              placeholder="Sales Order ID"
                              className="mt-1 w-40 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1 text-xs text-[var(--text-primary)] focus:border-purple-500 focus:outline-none"
                            />
                            <button
                              onClick={() => handleReplenishmentTrigger(item, '5_percent')}
                              disabled={isTriggering || !(customSoByProduct[item.productId] || '').trim()}
                              className="mt-1 flex items-center gap-1 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-purple-700 hover:bg-purple-100 disabled:opacity-50 transition-colors"
                              title="Run 5% SO-driven exact replenishment trigger"
                            >
                              {isTriggering ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                              Trigger 5% SO Replenishment
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {triggerResult && (
                      <div className="mt-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-muted)] px-3 py-2 text-xs text-[var(--text-secondary)]">
                        <span className="font-semibold text-[var(--text-primary)]">Trigger Result:</span> {triggerResult}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── View: GRN Entry ── */}
      {view === 'grn' && (
        <div className="max-w-2xl">
          {grnSuccess && (
            <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle size={18} />
                <p className="font-semibold">GRN {grnSuccess.grnNumber} Created</p>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-green-800">
                <span>Received: <strong>{grnSuccess.receivedQty}</strong></span>
                <span>Accepted: <strong>{grnSuccess.acceptedQty}</strong></span>
                <span>Rejected: <strong>{grnSuccess.rejectedQty}</strong></span>
                <span>Status: {inspectionBadge(grnSuccess.inspectionStatus)}</span>
              </div>
              {grnSuccess.stockUpdated && (
                <p className="mt-2 text-xs text-green-600 font-medium">✓ Stock updated automatically</p>
              )}
              <button onClick={() => setGrnSuccess(null)} className="mt-2 text-xs text-green-700 underline">Create another GRN</button>
            </div>
          )}

          <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
            <div className="mb-5 flex items-center gap-2">
              <PackageCheck size={18} className="text-[var(--ink-0)]" />
              <h3 className="text-base font-semibold text-[var(--text-primary)]">Goods Receipt Note — Incoming Inspection</h3>
            </div>

            <div className="space-y-4">
              {/* Product */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Product *</label>
                  <select
                    value={grnForm.productId}
                    onChange={(e) => {
                      const level = stockLevels.find((s) => s.productId === e.target.value);
                      setGrnForm((f) => ({
                        ...f,
                        productId: e.target.value,
                        productName: level?.productName || '',
                      }));
                    }}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  >
                    <option value="">Select product</option>
                    {stockLevels.map((s) => (
                      <option key={s.productId} value={s.productId}>{s.productName} {s.productSku ? `(${s.productSku})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Vendor</label>
                  <select
                    value={grnForm.vendorId}
                    onChange={(e) => {
                      const v = vendors.find((vv) => vv.id === e.target.value);
                      setGrnForm((f) => ({ ...f, vendorId: e.target.value, vendorName: v?.name || '' }));
                    }}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  >
                    <option value="">Select vendor (optional)</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Quantities */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Received Qty *</label>
                  <input
                    type="number" min={0} value={grnForm.receivedQty}
                    onChange={(e) => setGrnForm((f) => ({ ...f, receivedQty: e.target.value, inspectedQty: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Inspected Qty</label>
                  <input
                    type="number" min={0} value={grnForm.inspectedQty}
                    onChange={(e) => setGrnForm((f) => ({ ...f, inspectedQty: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Rejected Qty</label>
                  <input
                    type="number" min={0} value={grnForm.rejectedQty}
                    onChange={(e) => setGrnForm((f) => ({ ...f, rejectedQty: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Accepted auto-display */}
              {grnForm.inspectedQty && (
                <div className="rounded-lg bg-[var(--bg-muted)] px-4 py-2 text-sm">
                  <span className="text-[var(--text-muted)]">Accepted Qty = </span>
                  <strong className="text-green-600">
                    {Math.max(0, (parseFloat(grnForm.inspectedQty) || 0) - (parseFloat(grnForm.rejectedQty) || 0))} pcs
                  </strong>
                  <span className="ml-3 text-[var(--text-muted)]">→ will be added to stock</span>
                </div>
              )}

              {/* Inspection status */}
              <div>
                <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Inspection Status *</label>
                <div className="flex gap-3">
                  {(['pass', 'partial', 'fail'] as const).map((s) => (
                    <label key={s} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                      grnForm.inspectionStatus === s
                        ? s === 'pass' ? 'border-green-400 bg-green-50 text-green-700'
                          : s === 'partial' ? 'border-amber-400 bg-amber-50 text-amber-700'
                          : 'border-red-400 bg-red-50 text-red-700'
                        : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]'
                    }`}>
                      <input type="radio" name="inspectionStatus" value={s} checked={grnForm.inspectionStatus === s} onChange={() => setGrnForm((f) => ({ ...f, inspectionStatus: s }))} className="sr-only" />
                      {s === 'pass' ? 'Pass' : s === 'partial' ? 'Partial' : 'Fail'}
                    </label>
                  ))}
                </div>
              </div>

              {/* Personnel */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Received By *</label>
                  <input
                    type="text" value={grnForm.receivedBy}
                    onChange={(e) => setGrnForm((f) => ({ ...f, receivedBy: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="Name of person receiving goods"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Inspector Name</label>
                  <input
                    type="text" value={grnForm.inspectorName}
                    onChange={(e) => setGrnForm((f) => ({ ...f, inspectorName: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="QC inspector"
                  />
                </div>
              </div>

              {/* PO Reference & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">PO Reference</label>
                  <input
                    type="text" value={grnForm.poReference}
                    onChange={(e) => setGrnForm((f) => ({ ...f, poReference: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="e.g. PO-0042"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Notes</label>
                  <input
                    type="text" value={grnForm.notes}
                    onChange={(e) => setGrnForm((f) => ({ ...f, notes: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="Any remarks"
                  />
                </div>
              </div>

              {grnForm.inspectionStatus === 'fail' && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <strong>Fail:</strong> Stock will NOT be updated. All received goods are rejected.
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  onClick={handleGrnSubmit}
                  disabled={saving || !grnForm.productId || !grnForm.receivedQty || !grnForm.receivedBy}
                  className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50 transition-colors"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <PackageCheck size={15} />}
                  Submit GRN & Update Stock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── View: GRN History ── */}
      {view === 'history' && (
        <div>
          {grnHistory.length === 0 ? (
            <EmptyState icon={ClipboardList} text="No GRNs created yet" sub="Submit a GRN from the GRN Entry tab" />
          ) : (
            <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-muted)] text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                      <th className="px-4 py-3">GRN No.</th>
                      <th className="px-4 py-3">Product</th>
                      <th className="px-4 py-3">Vendor</th>
                      <th className="px-4 py-3 text-right">Received</th>
                      <th className="px-4 py-3 text-right">Accepted</th>
                      <th className="px-4 py-3 text-right">Rejected</th>
                      <th className="px-4 py-3">Inspection</th>
                      <th className="px-4 py-3">Stock</th>
                      <th className="px-4 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-primary)]">
                    {grnHistory.map((g, i) => (
                      <tr key={i} className="hover:bg-[var(--bg-surface-hover)] transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-[var(--text-primary)]">{g.grnNumber}</td>
                        <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{g.productName}</td>
                        <td className="px-4 py-3 text-[var(--text-secondary)]">{g.vendorName || '—'}</td>
                        <td className="px-4 py-3 text-right text-[var(--text-primary)]">{g.receivedQty}</td>
                        <td className="px-4 py-3 text-right font-semibold text-green-600">{g.acceptedQty}</td>
                        <td className="px-4 py-3 text-right text-red-600">{g.rejectedQty}</td>
                        <td className="px-4 py-3">{inspectionBadge(g.inspectionStatus)}</td>
                        <td className="px-4 py-3">
                          {g.stockUpdated
                            ? <span className="inline-flex items-center gap-1 text-xs text-green-600"><CheckCircle size={12} />Updated</span>
                            : <span className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]"><XCircle size={12} />Not updated</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-[var(--text-muted)]">{new Date(g.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Modal */}
      {settingsTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSettingsTarget(null)}>
          <div className="mx-4 w-full max-w-md rounded-xl bg-[var(--bg-surface)] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-6 py-4">
              <h3 className="font-semibold text-[var(--text-primary)]">Replenishment Settings</h3>
              <button onClick={() => setSettingsTarget(null)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <p className="text-sm font-medium text-[var(--text-primary)]">{settingsTarget.productName}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Min Stock Level</label>
                  <input
                    type="number" min={0} value={settingsForm.minStock}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, minStock: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="e.g. 10"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Max Stock Level</label>
                  <input
                    type="number" min={0} value={settingsForm.maxStock}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, maxStock: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    placeholder="e.g. 100"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Order Rule</label>
                <div className="flex gap-3">
                  <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${settingsForm.orderType === 'standard' ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-[var(--border-primary)] text-[var(--text-secondary)]'}`}>
                    <input type="radio" name="orderType" value="standard" checked={settingsForm.orderType === 'standard'} onChange={() => setSettingsForm((f) => ({ ...f, orderType: 'standard' }))} className="sr-only" />
                    95% Standard (Min/Max)
                  </label>
                  <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${settingsForm.orderType === 'custom' ? 'border-purple-400 bg-purple-50 text-purple-700' : 'border-[var(--border-primary)] text-[var(--text-secondary)]'}`}>
                    <input type="radio" name="orderType" value="custom" checked={settingsForm.orderType === 'custom'} onChange={() => setSettingsForm((f) => ({ ...f, orderType: 'custom' }))} className="sr-only" />
                    5% Custom (SO-Driven)
                  </label>
                </div>
              </div>
              {settingsForm.orderType === 'standard' && (
                <div>
                  <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">Fixed Vendor (for Auto-PO)</label>
                  <select
                    value={settingsForm.fixedVendorId}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, fixedVendorId: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  >
                    <option value="">No fixed vendor (send quotation to multiple)</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[var(--border-primary)] px-6 py-4">
              <button onClick={() => setSettingsTarget(null)} className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]">Cancel</button>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared Components ────────────────────────────────────────────────────────

function LoadingState({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      <span className="ml-2 text-sm text-gray-500">{text}</span>
    </div>
  );
}

function ErrorState({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-red-500">
      <p className="text-sm">{text}</p>
      <button onClick={onRetry} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
    </div>
  );
}

function EmptyState({ icon: Icon, text, sub }: { icon: any; text: string; sub: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
      <Icon size={40} className="mb-2" />
      <p className="text-sm">{text}</p>
      <p className="text-xs">{sub}</p>
    </div>
  );
}

// ─── Raw Material / SFG / FG Tabs ─────────────────────────────────────────────

// 🔒 2026-09-21: the tab is the shared Raw Materials panel (status, add, approve).
function RawMaterialTab() {
  return <RawMaterialsPanel />;
}

function SfgFgTab({ mode }: { mode: 'sfg' | 'fg' }) {
  const [rows, setRows] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get('/manufacturing/inventory/sfg-fg');
        setRows(res.data.data?.rows || []);
        setTotals(res.data.data?.totals || null);
      } catch (e: any) {
        setError(e.response?.data?.error || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[var(--ink-0)]" /></div>;
  if (error) return <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800">{error}</div>;

  const filtered = rows.filter((r) => mode === 'fg' ? Number(r.finishedQty || 0) > 0 : Number(r.semiFinishedQty || 0) > 0);
  const title = mode === 'fg' ? 'Finished Goods' : 'Semi-Finished Goods';
  const qtyKey = mode === 'fg' ? 'finishedQty' : 'semiFinishedQty';

  if (filtered.length === 0) return <EmptyState icon={mode === 'fg' ? PackageCheck : Activity} text={`No ${title.toLowerCase()}`} sub="Populated from production orders" />;

  return (
    <div>
      {totals && (
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-xs text-gray-500">Total Finished</div>
            <div className="text-2xl font-bold text-green-700">{totals.totalFinished || 0}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-xs text-gray-500">Total Semi-Finished</div>
            <div className="text-2xl font-bold text-amber-700">{totals.totalSemiFinished || 0}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-xs text-gray-500">Products Tracked</div>
            <div className="text-2xl font-bold text-gray-900">{rows.length}</div>
          </div>
        </div>
      )}
      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">Product</th>
              <th className="px-4 py-3 text-left">SKU</th>
              <th className="px-4 py-3 text-right">{title}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((r) => (
              <tr key={r.product?.id || r.productId}>
                <td className="px-4 py-3 font-medium text-gray-900">{r.product?.name || r.productName || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{r.product?.sku || '—'}</td>
                <td className="px-4 py-3 text-right font-semibold">{Number(r[qtyKey] || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Mismanaged Stock Alerts Widget (Task 6) ─────────────────────────────────

interface AlertItem {
  id: string;
  productName: string;
  productSku?: string;
  type: 'below_min' | 'dead_stock' | 'negative_movement';
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

function MismanagedStockAlerts() {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const alertsList: AlertItem[] = [];

      // 1. Below minimum stock (from replenishment endpoint)
      const [replenishRes, stockLevelsRes, movementsRes] = await Promise.all([
        api.get('/warehouse/inventory/replenishment').catch(() => ({ data: { data: [] } })),
        api.get('/warehouse/inventory/stock-levels').catch(() => ({ data: { data: [] } })),
        api.get('/warehouse/stock-movements', { params: { limit: 100 } }).catch(() => ({ data: { data: [] } })),
      ]);

      // Below min alerts
      const belowMin = replenishRes.data.data || [];
      for (const item of belowMin.slice(0, 10)) {
        alertsList.push({
          id: `min-${item.productId}`,
          productName: item.productName || 'Unknown',
          productSku: item.productSku,
          type: 'below_min',
          severity: 'critical',
          message: `Stock: ${item.quantity} / Min: ${item.minStock} (shortage: ${item.shortage})`,
        });
      }

      // 2. Dead stock — items with no movement in 30+ days
      const stockLevels = stockLevelsRes.data.data || [];
      const movements = movementsRes.data.data || [];
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const recentlyMovedProducts = new Set(
        movements
          .filter((m: any) => new Date(m.createdAt) >= thirtyDaysAgo)
          .map((m: any) => m.productId || m.product?.id)
          .filter(Boolean)
      );

      for (const item of stockLevels) {
        if (Number(item.quantity) > 0 && !recentlyMovedProducts.has(item.productId)) {
          alertsList.push({
            id: `dead-${item.productId}`,
            productName: item.productName || 'Unknown',
            productSku: item.productSku,
            type: 'dead_stock',
            severity: 'warning',
            message: `${Number(item.quantity)} ${item.unit || 'units'} in stock, no movement in 30+ days`,
          });
        }
      }

      // 3. Recent negative movements (potential mismanagement — large outgoing adjustments)
      const negativeAdjustments = movements.filter(
        (m: any) => m.type === 'adjustment' && Number(m.quantity) < 0
      );
      for (const m of negativeAdjustments.slice(0, 5)) {
        alertsList.push({
          id: `neg-${m.id}`,
          productName: m.product?.name || m.productName || 'Unknown',
          productSku: m.product?.sku,
          type: 'negative_movement',
          severity: 'info',
          message: `Negative adjustment: ${m.quantity} (${m.reason || 'no reason given'})`,
        });
      }

      setAlerts(alertsList);
    } catch {
      // Non-critical — silently fail
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (alerts.length === 0) return null;

  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning').length;

  const SEVERITY_STYLES: Record<string, string> = {
    critical: 'border-l-red-500 bg-red-50',
    warning: 'border-l-yellow-500 bg-yellow-50',
    info: 'border-l-blue-500 bg-blue-50',
  };

  const SEVERITY_ICON: Record<string, React.ReactNode> = {
    critical: <AlertTriangle className="h-4 w-4 text-red-500" />,
    warning: <Clock className="h-4 w-4 text-yellow-500" />,
    info: <Activity className="h-4 w-4 text-blue-500" />,
  };

  const TYPE_LABELS: Record<string, string> = {
    below_min: 'Below Minimum',
    dead_stock: 'Dead Stock',
    negative_movement: 'Negative Adjustment',
  };

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <span className="font-semibold text-gray-900">Stock Alerts</span>
          {criticalCount > 0 && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              {criticalCount} critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
              {warningCount} warning
            </span>
          )}
        </div>
        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
      </button>

      {!collapsed && (
        <div className="border-t border-gray-100 px-4 pb-3 pt-2">
          <div className="max-h-[300px] space-y-2 overflow-y-auto">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`flex items-start gap-3 rounded border-l-4 px-3 py-2 text-sm ${SEVERITY_STYLES[alert.severity]}`}
              >
                <div className="mt-0.5">{SEVERITY_ICON[alert.severity]}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{alert.productName}</span>
                    {alert.productSku && <span className="text-xs text-gray-500">{alert.productSku}</span>}
                    <span className={`ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      alert.type === 'below_min' ? 'bg-red-100 text-red-600'
                        : alert.type === 'dead_stock' ? 'bg-yellow-100 text-yellow-600'
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {TYPE_LABELS[alert.type]}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">{alert.message}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
